import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AVAILABLE_LENSES = [
  { id: 'jungian', name: 'Jungian Psychology', desc: 'Archetypes, shadow, collective unconscious' },
  { id: 'buddhism', name: 'Buddhism', desc: 'Non-attachment, mindfulness, suffering' },
  { id: 'taoism', name: 'Taoism', desc: 'Wu wei, balance, natural flow' },
  { id: 'christianity', name: 'Christianity', desc: 'Grace, redemption, sacrifice' },
  { id: 'hinduism', name: 'Hinduism', desc: 'Dharma, karma, divine manifestation' },
  { id: 'sufism', name: 'Sufism/Mysticism', desc: 'Divine love, ecstatic union, surrender' },
  { id: 'greek-myth', name: 'Greek Mythology', desc: 'Heroic journeys, gods, fate' },
  { id: 'norse-myth', name: 'Norse Mythology', desc: 'Warriors, destiny, cosmic cycles' },
  { id: 'fairy-tales', name: 'Fairy Tale Archetypes', desc: 'Transformation, trials, magic' },
  { id: 'fantasy-lit', name: 'Fantasy Literature', desc: 'Quests, chosen ones, world-building' },
  { id: 'stoicism', name: 'Stoicism', desc: 'Virtue, acceptance, resilience' },
  { id: 'science', name: 'Biology/Science', desc: 'Natural patterns, evolution, systems' }
];

const TONE_OPTIONS = [
  { id: 'mystical', name: 'Mystical', desc: 'Poetic, symbolic, transcendent' },
  { id: 'grounded', name: 'Grounded', desc: 'Practical, earthy, direct' },
  { id: 'scholarly', name: 'Scholarly', desc: 'Intellectual, analytical, precise' },
  { id: 'poetic', name: 'Poetic', desc: 'Lyrical, metaphorical, flowing' }
];

const STRUCTURE_OPTIONS = [
  { id: 'multi-lens', name: 'Multi-Lens', desc: 'Separate sections for each wisdom lens' },
  { id: 'unified', name: 'Unified', desc: 'Blended devotional weaving all lenses' }
];

export default function ProfileSetup({ user }) {
  const navigate = useNavigate();
  const [selectedLenses, setSelectedLenses] = useState([]);
  const [tone, setTone] = useState('mystical');
  const [structure, setStructure] = useState('multi-lens');
  const [loading, setLoading] = useState(false);

  const toggleLens = (lensId) => {
    if (selectedLenses.includes(lensId)) {
      setSelectedLenses(selectedLenses.filter(id => id !== lensId));
    } else {
      if (selectedLenses.length >= 6) {
        toast.error('Maximum 6 lenses allowed');
        return;
      }
      setSelectedLenses([...selectedLenses, lensId]);
    }
  };

  const handleSubmit = async () => {
    if (selectedLenses.length < 2) {
      toast.error('Please select at least 2 lenses');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/profile/setup`, {
        selected_lenses: selectedLenses.map(id => AVAILABLE_LENSES.find(l => l.id === id).name),
        tone_preference: tone,
        reading_structure: structure
      });
      toast.success('Profile created!');
      navigate('/dashboard');
    } catch (err) {
      toast.error('Failed to create profile');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container">
      <div className="fade-in" style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <BookOpen size={64} color="#d4af37" style={{ margin: '0 auto 1rem' }} />
          <h1>Choose Your Wisdom Lenses</h1>
          <p style={{ color: '#c9c9c9', marginTop: '1rem' }}>
            Welcome, {user?.name}. Select 2-6 traditions that resonate with your soul.
          </p>
        </div>

        {/* Lens Selection */}
        <div className="mystical-card" style={{ marginBottom: '2rem' }}>
          <h2 style={{ marginBottom: '1.5rem' }}>Available Lenses ({selectedLenses.length}/6)</h2>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '1rem'
          }}>
            {AVAILABLE_LENSES.map(lens => (
              <div
                key={lens.id}
                data-testid={`lens-${lens.id}`}
                className={`lens-card ${selectedLenses.includes(lens.id) ? 'selected' : ''}`}
                onClick={() => toggleLens(lens.id)}
              >
                <h3 style={{ fontSize: '1.1rem', marginBottom: '0.5rem', color: '#f4d03f' }}>
                  {lens.name}
                </h3>
                <p style={{ fontSize: '0.9rem', color: '#999' }}>{lens.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Tone Selection */}
        <div className="mystical-card" style={{ marginBottom: '2rem' }}>
          <h2 style={{ marginBottom: '1.5rem' }}>Reading Tone</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
            {TONE_OPTIONS.map(t => (
              <div
                key={t.id}
                data-testid={`tone-${t.id}`}
                className={`lens-card ${tone === t.id ? 'selected' : ''}`}
                onClick={() => setTone(t.id)}
                style={{ padding: '1rem' }}
              >
                <h3 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>{t.name}</h3>
                <p style={{ fontSize: '0.85rem', color: '#999' }}>{t.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Structure Selection */}
        <div className="mystical-card" style={{ marginBottom: '2rem' }}>
          <h2 style={{ marginBottom: '1.5rem' }}>Reading Structure</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
            {STRUCTURE_OPTIONS.map(s => (
              <div
                key={s.id}
                data-testid={`structure-${s.id}`}
                className={`lens-card ${structure === s.id ? 'selected' : ''}`}
                onClick={() => setStructure(s.id)}
                style={{ padding: '1rem' }}
              >
                <h3 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>{s.name}</h3>
                <p style={{ fontSize: '0.85rem', color: '#999' }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Submit */}
        <div style={{ textAlign: 'center' }}>
          <Button 
            onClick={handleSubmit}
            data-testid="submit-profile-button"
            className="btn-gold"
            disabled={loading || selectedLenses.length < 2}
            style={{ fontSize: '1.2rem', padding: '1rem 3rem' }}
          >
            {loading ? 'Creating...' : 'Create My Profile'}
          </Button>
        </div>
      </div>
    </div>
  );
}